import { useRef, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';

type ControlFunctions = {
  /** Cancels pending function invocations. */
  cancel: () => void;
  /** Immediately invokes pending function invocations. */
  flush: () => void;
  isPending: () => boolean;
};

export type DebouncedState<T extends (...args: any[]) => any> = ((...args: Parameters<T>) => void) &
  ControlFunctions;

export function useDebounceCallback<T extends (...args: any[]) => any>(
  func: T,
  delay = 250
): DebouncedState<T> {
  const latestFuncRef = useRef(func);
  const pendingRef = useRef(false);

  useEffect(() => {
    latestFuncRef.current = func;
  }, [func]);

  const debounced = useMemo(() => {
    const debouncedFuncInstance = debounce((...args: Parameters<T>) => {
      pendingRef.current = false;
      latestFuncRef.current(...args);
    }, delay);

    const wrappedFunc: DebouncedState<T> = (...args: Parameters<T>) => {
      pendingRef.current = true;
      debouncedFuncInstance(...args);
    };

    wrappedFunc.cancel = () => {
      pendingRef.current = false;
      debouncedFuncInstance.cancel();
    };

    wrappedFunc.isPending = () => {
      return pendingRef.current;
    };

    wrappedFunc.flush = () => {
      pendingRef.current = false;
      debouncedFuncInstance.flush();
    };

    return wrappedFunc;
  }, [delay]);

  useEffect(() => {
    return () => {
      debounced.cancel();
    };
  }, [debounced]);

  return debounced;
}
